<template>
  <v-card
    class="routine-scoring-step pt-6 pb-3 px-8 ma-0"
    :class="{ 'rounded-t-0': isEditMode }"
  >
    <v-card-title class="pa-0 mb-2 d-flex justify-space-between align-top">
      <p class="ma-0 pa-0 h6 blue-grey--text text--darken-3">
        {{ $t('routines.scoring.title') }}
      </p>
    </v-card-title>

    <v-card-subtitle
      v-html="$t('routines.scoring.subtitle')"
      class="pa-0 ma-0 blue-grey--text text--darken-1 body-1"
    />

    <v-divider class="my-6" />

    <ScoringModeSelector
      :value="localScoringType"
      @change="handleScoringTypeChange"
    />

    <v-row>
      <v-col cols="12" md="8" class="pr-md-4">
        <v-form
          v-if="localScoringType !== null"
          v-model="isScoringFormValid"
          ref="scoringForm"
          lazy-validation
        >
          <section v-if="localScoringType === SCORING_MODES.QUESTION">
            <template v-for="(row, rowIndex) in questionModeRows">
              <v-card
                v-if="row.kind === 'separator'"
                data-testid="scoringStep-card-sectionHeader"
                class="section-header-card section-header-card--primary px-6 py-5 mb-6 d-flex align-center justify-space-between"
                min-height="72"
                outlined
                :key="row.item.id || row.item._id || `separator-${rowIndex}`"
                :style="row.sectionStyle"
              >
                <span
                  class="body-1 font-weight-600 blue-grey--text text--darken-3"
                >
                  {{ `${row.item.name || defaultSectionLabel}` }}
                </span>
              </v-card>

              <QuestionScoringCard
                v-else
                data-testid="scoringStep-questionScoringCard-question"
                :key="
                  row.item.scoringItemId ||
                  row.item._id ||
                  row.item.id ||
                  `question-${row.questionIndex}`
                "
                :active="isQuestionActive(row.item.scoringItemId)"
                :item="row.item"
                :index="row.questionIndex"
                :isInsideSection="row.inSection"
                :borderColor="row.sectionColor"
                @activateQuestion="handleActivateQuestion"
                @change="handleQuestionScoringChange"
              />
            </template>
          </section>

          <template v-if="localScoringType === SCORING_MODES.SECTION">
            <section
              v-for="(sectionGroup, sectionIndex) in sectionScoringGroups"
              :key="sectionGroup.id || sectionIndex"
            >
              <SectionScoringGroup
                data-testid="scoringStep-sectionScoringGroup-section"
                :activeQuestionId="activeQuestionId"
                :activeSection="isSectionActive(sectionGroup.itemId)"
                :section="sectionGroup"
                :sectionIndex="sectionIndex"
                :borderColor="getChecklistSectionColor(sectionIndex)"
                :fallbackTitle="defaultSectionLabel"
                @activateQuestion="handleActivateQuestion"
                @activateSection="handleActivateSection"
                @weightChange="handleSectionWeightChange"
                @questionChange="handleQuestionScoringChange"
              />
            </section>
          </template>
        </v-form>
      </v-col>

      <v-col v-if="showSummary" cols="12" md="4">
        <aside class="summary-column">
          <ScoringSummaryCard
            data-testid="scoringStep-scoringSummaryCard-summary"
            :hasItemsWithVisibilityRules="hasItemsWithVisibilityRules"
            :sections="displaySections"
            :summary="summary"
          />

          <Alert
            v-if="validationState.generalErrors.length"
            class="mt-4 py-3"
            textClass="body-2"
            :vuetifyProps="{
              color: 'error',
              icon: 'mdi-alert-circle-outline',
            }"
            :text="scoringValidationAlertText"
          />
        </aside>
      </v-col>
    </v-row>

    <v-card-actions class="pa-0 mb-0 pt-4 actions-container pb-3 px-0">
      <div v-if="isEditMode" class="d-flex justify-end w-100">
        <Button
          plain
          class="me-4"
          color="grey"
          :disabled="isSaving"
          :text="$t('common.button.cancel')"
          @click="$emit('cancel')"
        />
        <Button
          color="primary"
          class="white--text"
          :disabled="isSaving"
          :loading="isSaving"
          :text="$t('common.button.save')"
          @click="handleSave"
        />
      </div>

      <div v-else class="d-flex justify-end w-100">
        <Button
          class="me-2"
          color="grey"
          icon="mdi-chevron-left"
          plain
          :disabled="isSaving"
          :text="$t('common.button.back')"
          @click="$emit('back')"
        />
        <Button
          plain
          iconPosition="right"
          color="primary"
          icon="mdi-chevron-right"
          class="white--text"
          :disabled="isSaving"
          :loading="isSaving"
          :text="$t('common.button.next')"
          @click="handleNext"
        />
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
import Alert from '@/components/Alert.vue'
import Button from '@/components/UI/Button'
import { utils } from 'frontend-js-shared'
import { SCORING_ITEM_TYPES, SCORING_MODES } from '@/constants'
import { getChecklistSectionColor } from '@/lib/getChecklistSectionColor'

import {
  activateQuestionTarget,
  activateSectionTarget,
  createInactiveScoringTarget,
  isQuestionTargetActive,
  isSectionTargetActive
} from './scoring/activeScoringTarget'
import { hasOrphanQuestions } from './scoring/sectionCompatibility'
import { resolveScoringItemId } from './scoring/scoringShared'
import {
  getQuestionModeValidationErrors,
  getSectionModeValidationErrors
} from './scoring/scoringValidation'
import {
  DEFAULT_SECTION_NAME_KEY,
  calculateChecklistMaxScore,
  buildScoringSectionsFromItems,
  buildSectionScoringGroupsFromItems,
  ensureSectionSeparatorForScoring
} from './scoring/scoringViewModel'
import QuestionScoringCard from './QuestionScoringCard.vue'
import ScoringModeSelector from './ScoringModeSelector.vue'
import ScoringSummaryCard from './ScoringSummaryCard.vue'
import SectionScoringGroup from './SectionScoringGroup.vue'

export default {
  name: 'ScoringStep',

  components: {
    Button,
    Alert,
    QuestionScoringCard,
    ScoringModeSelector,
    ScoringSummaryCard,
    SectionScoringGroup,
  },

  props: {
    checklist: {
      type: Object,
      default: () => ({ items: [] }),
    },
    scoringState: {
      type: Object,
      default: () => ({ scoringType: null }),
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      localScoringType:
        this.scoringState?.scoringType ?? this.checklist?.scoringType ?? null,
      validationState: {
        isValid: true,
        generalErrors: [],
      },
      activeScoringTarget: createInactiveScoringTarget(),
      isScoringFormValid: true,
      SCORING_MODES,
    }
  },

  mounted() {
    this.runValidation()
  },

  watch: {
    scoringState: {
      deep: true,
      handler(newScoringState) {
        if (newScoringState?.scoringType !== undefined) {
          this.localScoringType = newScoringState?.scoringType ?? null
        }
      },
    },
    checklist: {
      deep: true,
      handler(newChecklist) {
        if (
          this.localScoringType === null &&
          newChecklist?.scoringType !== undefined
        ) {
          this.localScoringType = newChecklist?.scoringType ?? null
          return
        }

        this.runValidation()
      },
    },
    localScoringType: {
      handler() {
        this.runValidation()
      },
    },
    isScoringFormValid: {
      handler() {
        this.runValidation()
      },
    },
  },

  computed: {
    scoringSubtitle() {
      if (this.localScoringType === SCORING_MODES.QUESTION) {
        return this.$t('routines.scoring.subtitle_question')
      }

      if (this.localScoringType === SCORING_MODES.SECTION) {
        return this.$t('routines.scoring.subtitle_section')
      }

      return this.$t('routines.scoring.subtitle_default')
    },

    showSummary() {
      return this.localScoringType !== null
    },

    defaultSectionLabel() {
      return this.$t(DEFAULT_SECTION_NAME_KEY)
    },

    displaySections() {
      if (this.localScoringType !== SCORING_MODES.SECTION) {
        return []
      }

      return buildScoringSectionsFromItems(this.checklist?.items || [])
    },

    questionModeRows() {
      const sourceItems = this.checklist?.items || []
      let separatorIndex = 0
      let questionIndex = 0
      let inSection = false
      let currentSectionColor = null

      return sourceItems.reduce((rows, item, sourceIndex) => {
        if (item?.type === SCORING_ITEM_TYPES.SEPARATOR) {
          inSection = true
          currentSectionColor = getChecklistSectionColor(separatorIndex)
          rows.push({
            kind: 'separator',
            separatorIndex,
            item,
            sectionColor: currentSectionColor,
            sectionStyle: currentSectionColor
              ? `border-left-width: 4px !important; border-left-color: ${currentSectionColor} !important;`
              : '',
          })
          separatorIndex += 1
          return rows
        }

        rows.push({
          kind: 'question',
          questionIndex,
          inSection,
          sectionColor: inSection ? currentSectionColor : null,
          item: {
            ...item,
            scoringItemId: this.resolveItemId(
              item,
              `scoring-item-${sourceIndex}`
            ),
            scoring: {
              enabled: true,
              ...(item?.scoring || {}),
            },
          },
        })
        questionIndex += 1
        return rows
      }, [])
    },

    sectionScoringGroups() {
      if (this.localScoringType !== SCORING_MODES.SECTION) {
        return []
      }

      return buildSectionScoringGroupsFromItems(this.checklist?.items || [])
    },

    hasItemsWithVisibilityRules() {
      return (this.checklist?.items || []).some(item =>
        utils.checklists.hasVisibilityRule(item)
      )
    },

    summary() {
      return {
        scorePercentage: null,
        totalPoints: calculateChecklistMaxScore(this.checklist?.items || []),
      }
    },

    scoringValidationAlertText() {
      return this.validationState.generalErrors
        .map(error => this.$t(`routines.scoring.${error}`))
        .join('<br>')
    },

    activeQuestionId() {
      return this.activeScoringTarget?.type === 'question'
        ? this.activeScoringTarget.id
        : null
    },
  },

  methods: {
    resolveItemId(item, fallbackId = null) {
      return resolveScoringItemId(item, fallbackId)
    },

    isQuestionActive(itemId) {
      return isQuestionTargetActive(this.activeScoringTarget, itemId)
    },

    isSectionActive(sectionId) {
      return isSectionTargetActive(this.activeScoringTarget, sectionId)
    },

    shouldRequestSectionRecovery(scoringType, items) {
      return scoringType === SCORING_MODES.SECTION && hasOrphanQuestions(items)
    },

    requestSectionRecovery(scoringType, items) {
      this.$emit('requestDefaultSection', {
        items,
        scoringType,
        source: 'scoring',
      })
    },

    getItemsForScoringTypeChange(scoringType, items) {
      if (scoringType !== SCORING_MODES.SECTION) {
        return items
      }

      return ensureSectionSeparatorForScoring(items, this.defaultSectionLabel)
        .items
    },

    emitScoringTypeChecklistChange(scoringType, items) {
      const validationState = this.applyValidationState(items, scoringType)

      this.$emit('checklistChange', {
        items: validationState.normalizedItems,
        scoringType,
      })
    },

    handleScoringTypeChange(value) {
      const sourceItems = [...(this.checklist?.items || [])]

      if (this.shouldRequestSectionRecovery(value, sourceItems)) {
        this.requestSectionRecovery(value, sourceItems)
        return
      }

      const normalizedItems = this.getItemsForScoringTypeChange(
        value,
        sourceItems
      )

      this.emitScoringTypeChecklistChange(value, normalizedItems)

      this.localScoringType = value
      this.activeScoringTarget = createInactiveScoringTarget()
      this.$emit('scoringTypeChange', value)
      this.$nextTick(() => this.validateScoringForm())
    },

    handleActivateQuestion(itemId) {
      this.activeScoringTarget = activateQuestionTarget(itemId)
    },

    handleActivateSection(sectionId) {
      this.activeScoringTarget = activateSectionTarget(sectionId)
    },

    handleQuestionScoringChange(payload) {
      const { itemId } = payload
      const updatedItems = this.updateQuestionItems(itemId, payload)

      const validationState = this.applyValidationState(
        updatedItems,
        this.localScoringType
      )

      this.$emit('checklistChange', {
        items: validationState.normalizedItems,
        scoringType: this.localScoringType,
      })
    },

    updateQuestionItems(itemId, { scoring, options }) {
      return (this.checklist?.items || []).map((item, index) => {
        if (item?.type === SCORING_ITEM_TYPES.SEPARATOR) {
          return item
        }

        const currentId = this.resolveItemId(item, `scoring-item-${index}`)
        if (currentId !== itemId) {
          return item
        }

        return {
          ...item,
          ...(options ? { options } : {}),
          scoring: {
            enabled: true,
            ...(item?.scoring || {}),
            ...(scoring || {}),
          },
        }
      })
    },

    handleSectionWeightChange(payload) {
      const { sectionItemId, weightPercentage } = payload
      const sourceItems = [...(this.checklist?.items || [])]

      const updatedItems = sourceItems.map((item, index) => {
        if (item?.type !== 'separator') {
          return item
        }

        const currentId = this.resolveItemId(item, `scoring-item-${index}`)
        if (currentId !== sectionItemId) {
          return item
        }

        return {
          ...item,
          scoring: {
            ...item?.scoring,
            weightPercentage,
          },
        }
      })

      const validationState = this.applyValidationState(
        updatedItems,
        this.localScoringType
      )

      this.$emit('checklistChange', {
        items: validationState.normalizedItems,
        scoringType: this.localScoringType,
      })
    },

    runValidation() {
      const items = this.checklist?.items || []
      this.applyValidationState(items, this.localScoringType)
    },

    applyValidationState(items, scoringType) {
      if (!scoringType) {
        const validationState = {
          isValid: true,
          generalErrors: [],
          normalizedItems: items,
        }

        this.validationState = validationState
        this.$emit('scoringValidationChange', validationState)
        return validationState
      }

      const generalErrors =
        scoringType === SCORING_MODES.QUESTION
          ? getQuestionModeValidationErrors(items)
          : getSectionModeValidationErrors(items)

      const validationState = {
        isValid: this.isScoringFormValid && !generalErrors.length,
        generalErrors,
        normalizedItems: items,
      }

      this.validationState = validationState

      this.$emit('scoringValidationChange', this.validationState)

      return validationState
    },

    handleNext() {
      this.validateScoringForm()
      this.runValidation()

      if (!this.validationState.isValid) {
        return
      }

      this.$emit('next')
    },

    handleSave() {
      this.validateScoringForm()
      this.runValidation()

      if (!this.validationState.isValid) {
        this.$emit('invalidSave', this.validationState)
        return
      }

      this.$emit('save')
    },

    validateScoringForm() {
      if (!this.$refs.scoringForm) {
        this.isScoringFormValid = true
        return true
      }

      const isValid = this.$refs.scoringForm.validate()
      this.isScoringFormValid = isValid
      return isValid
    },

    getChecklistSectionColor,
  },
}
</script>

<style scoped lang="scss">
.summary-column {
  position: sticky;
  top: 96px;
}

.section-header-card {
  border-color: $color-blue-grey-lighten2 !important;
  background-color: $color-white;
  border-left-width: 4px;
}

@media (max-width: 959px) {
  .summary-column {
    position: static;
  }
}

.actions-container {
  position: sticky;
  bottom: 0;
  background: linear-gradient(to bottom, transparent 10%, white 100%);
}
</style>
